// ─── Test phone numbers + the send switch (2026-08-13, rewritten 2026-08-14) ─
//
// UPDATED FOR THE CONVERSATIONAL REPLY PATH. Three things now decide whether
// a customer receives an automatic answer, and ALL THREE must be true:
//
//   1. "Préparer des réponses"  → app_settings.assistant_enabled      (Cerveau)
//   2. "Envoyer les réponses"   → app_settings.assistant_send_enabled (below)
//   3. the customer's number is in this list
//
// AN EMPTY LIST SENDS TO NOBODY — and as of 2026-08-18 that is true of BOTH
// send paths, which is the only reason this card is allowed to say it plainly.
//
// ⚠️ IT WAS HALF FALSE UNTIL THEN. `agent_test_numbers` gates two different
// doors. The conversational one (`isAllowlistedPhone`, worker/src/lib/
// agentReply.ts) has always read an empty list as "nobody". The confirmation-
// agent JOURNEY queue (`isTestNumberMatch`, worker/src/lib/agentOutbox.ts)
// read exactly the same empty list as "no per-chat restriction" — i.e.
// EVERYBODY. So this card told an owner who emptied the list that nobody could
// be messaged while order-journey messages kept going out to every customer.
// Both now read empty as nobody; going wide requires
// `app_settings.agent_unrestricted_enabled`, which has no control here on
// purpose (see the banner below, which says so when it is on).
//
// ─── AND WHAT, not just WHO (migration 111) ─────────────────────────────────
//
// The list above answers "which customers". The switches under "Ce que
// l'assistant peut envoyer tout seul" answer "which kinds of message" — one
// per ladder, all shipping OFF. Before them, adding a number switched on
// fourteen senders at once, including the one that prints the RIB.
//
// Why two switches and not one: the owner intends to run for days with
// drafting ON and sending OFF, reading what the model would have said without
// a single customer receiving it. One boolean cannot express that.
//
// Storage: `app_settings.agent_test_numbers` (text[]), read via the same
// `/settings` route every other admin setting uses (settingsApi). We store
// the canonicalised (`212XXXXXXXXX`) form so what's on screen is exactly
// what the backend will match — the backend re-canonicalises on every read
// regardless, but storing it clean avoids two different-looking entries
// that are secretly the same number.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { settingsApi, whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';

const inputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

// ── Icons (same 1.7-stroke language as AdminAssistantPage.tsx) ───────────────
type IconProps = { className?: string };
const Icon = ({ children, className = 'w-[18px] h-[18px]' }: IconProps & { children: React.ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true">
    {children}
  </svg>
);
const AlertIcon = (p: IconProps) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" /></Icon>;
const ShieldIcon = (p: IconProps) => <Icon {...p}><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /></Icon>;
const GlobeIcon = (p: IconProps) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-5.5-3.5-9s1-6.5 3.5-9z" /></Icon>;
const XIcon = (p: IconProps) => <Icon {...p}><path d="M18 6 6 18M6 6l12 12" /></Icon>;
const PlusIcon = (p: IconProps) => <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>;

/**
 * Mirrors `canonicalPhone` in worker/src/lib/phone.ts. Kept as a small
 * client-side copy for an instant preview as the owner types — the worker
 * is still the source of truth at send time, this is display-only.
 *
 *   0XXXXXXXXX (10) → 212XXXXXXXXX
 *   XXXXXXXXX  (9)  → 212XXXXXXXXX
 *   212XXXXXXXXX    → passthrough
 *   +212 XX XX XX XX→ strip spaces/+, passthrough
 */
function canonicalPhone(input: string): string | null {
  // ⚠️ INTERNATIONAL INTENT FIRST. « +971… » / « 00971… » must never fall into
  // the Moroccan branches below — « 00971… » starts with 0 and would become
  // « 2120971… », a number that matches nobody and silences the agent with no
  // visible cause (the exact failure the red warning under the field names).
  const trimmed = input.trim();
  if (/^(\+|00)/.test(trimmed)) {
    const digits = trimmed.replace(/^00/, '').replace(/\D/g, '');
    return digits || null;
  }
  const digits = input.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('212')) return digits;
  if (digits.startsWith('0')) return '212' + digits.slice(1);
  if (digits.length === 9) return '212' + digits;
  return digits;
}

/** A WhatsApp test number has to be a real Moroccan mobile — landlines and
 *  mistyped digit-strings canonicalise to *something*, but that something
 *  will never match an incoming chat, and a bad entry here means the gate
 *  matches nothing and the agent goes silent with no explanation. */
function isPlausibleMoroccanMobile(canon: string): boolean {
  return /^212[67]\d{8}$/.test(canon);
}

/**
 * A foreign number is welcome — the worker's matcher compares canonical
 * digits, and WhatsApp phone ids always carry the country code — but ONLY
 * when the country code was typed (+ or 00). A bare national number from
 * another country (« 056 379 7374 ») would canonicalise into a wrong Moroccan
 * number and match nobody; requiring the + keeps that failure impossible.
 */
function isPlausibleInternational(raw: string, canon: string): boolean {
  if (!/^(\+|00)/.test(raw.trim())) return false;
  if (canon.startsWith('212')) return isPlausibleMoroccanMobile(canon);
  // E.164: country code + subscriber, 8–15 digits, never 0-leading.
  return /^[1-9]\d{7,14}$/.test(canon);
}

function formatDisplay(canon: string): string {
  if (!/^212\d{9}$/.test(canon)) return canon.startsWith('212') ? canon : `+${canon}`;
  const rest = canon.slice(3);
  return `+212 ${rest.slice(0, 3)} ${rest.slice(3, 5)} ${rest.slice(5, 7)} ${rest.slice(7, 9)}`;
}

/** The switch row used by every operational toggle above.
 *
 *  `sensitive` is for the two switches that can cost real money or real trust:
 *  the payment reminder (it prints the bank details and the total) and raw
 *  AI-written replies. They are drawn amber ON and amber-outlined OFF so they
 *  never blend into the row of ordinary green checkboxes — an owner scanning
 *  this card has to be able to see, without reading, that two of these are not
 *  like the others. */
function Toggle({ checked, onChange, disabled, title, hint, sensitive = false }: {
  checked: boolean; onChange: (v: boolean) => void; disabled: boolean; title: string; hint: string;
  sensitive?: boolean;
}) {
  const border = sensitive
    ? (checked ? 'border-amber-300 bg-amber-50' : 'border-amber-200 bg-white')
    : (checked ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white');
  return (
    <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3.5 ${border}`}>
      <input
        type="checkbox" checked={checked} disabled={disabled}
        className={`mt-0.5 w-4 h-4 ${sensitive ? 'accent-amber-600' : 'accent-brand-600'}`}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block text-[13px] font-semibold text-gray-800">
          {sensitive && <AlertIcon className="w-3.5 h-3.5 text-amber-700 inline-block mr-1 -mt-px" />}
          {title}
        </span>
        <span className={`block text-[11.5px] mt-0.5 leading-snug ${sensitive ? 'text-amber-800' : 'text-gray-600'}`}>{hint}</span>
      </span>
    </label>
  );
}

/**
 * ONE SWITCH PER FAMILLE DE MESSAGE (migration 111).
 *
 * ⚠️ THE COLUMN NAMES ARE MIRRORED FROM worker/src/lib/agentReply.ts
 * (`LADDER_COLUMN`). `web/` cannot import from `worker/`, so a rename on one
 * side and not the other produces a checkbox that saves happily and is never
 * read — a switch that looks like it works and does nothing, which on the
 * payment row means bank details going out under a box the owner believes is
 * off. Grep both files together.
 *
 * ⚠️ `ladder_payment_enabled` COVERS BOTH RIB SENDERS — the first payment ask
 * (`milestone:payment_request`) and the chase (`followup:payment:N`). The
 * milestone switch below deliberately does NOT include the payment request,
 * for exactly the reason the label spells out.
 *
 * Wording rule for this list: say what the CUSTOMER receives, in the words the
 * owner would use, and name the risk in the label itself rather than in a
 * hint nobody reads.
 */
/**
 * The five stages a customer passes through, in the order they meet them.
 *
 * ─── WHY THIS SHAPE ─────────────────────────────────────────────────────────
 *
 * These were nine identical checkboxes in a vertical list. Read that way, the
 * only question you could answer was "is this one box ticked" — never "does a
 * customer actually get taken all the way through". Laid out as the journey,
 * the gap is the answer: a green Photos card followed by four grey ones says
 * the flow stops after the first message, which is the thing worth knowing.
 *
 * ⚠️ ONLY FIVE OF THE NINE ARE STAGES. Milestones, the owner's own relances and
 * the AI's free text are not steps a customer walks through — they happen
 * alongside. Forcing them into the strip would draw a journey that does not
 * exist, so they stay as plain rows underneath. Nothing is hidden either way.
 *
 * ⚠️ PHOTOS CARRIES TWO SWITCHES. The ask and "which album is this for?" are
 * one moment to a customer and two settings to us; both controls live inside
 * the opened card, so neither becomes unreachable.
 */
const JOURNEY: Array<{ key: string; label: string; columns: string[] }> = [
  { key: 'photos',  label: 'Photos',    columns: ['ladder_photos_enabled', 'ladder_which_album_enabled'] },
  { key: 'cover',   label: 'Couverture', columns: ['ladder_cover_enabled'] },
  { key: 'preview', label: 'Aperçu 3D',  columns: ['ladder_preview_enabled'] },
  { key: 'payment', label: 'Paiement',   columns: ['ladder_payment_enabled'] },
  { key: 'review',  label: 'Avis',       columns: ['ladder_review_enabled'] },
];

/** The three that are not journey stages — see the note on JOURNEY. */
const OFF_JOURNEY = ['ladder_milestones_enabled', 'ladder_rules_enabled', 'ladder_client_promise_enabled', 'ladder_ai_replies_enabled'];

const LADDER_ROWS: Array<{
  column: string;
  title: string;
  on: string;
  off: string;
  sensitive?: boolean;
  /** Shown in a confirm() before switching ON. Only the two sensitive ones. */
  confirmOn?: string;
}> = [
  {
    column: 'ladder_photos_enabled',
    // ⚠️ LE PREMIER MESSAGE N’EST PAS UNE RELANCE, c’est le reçu de la commande :
    // il part moins de 20 secondes après l’achat (PHOTO_LADDER_HOURS = [0, 24, 72]
    // dans worker/src/lib/followUpPhotos.ts) et relit au client son pack, son
    // format et son adresse avant de demander quoi que ce soit. L’ancien libellé
    // disait « le client est relancé » — en OFF (la valeur par défaut) l’owner
    // croyait donc couper des rappels, alors qu’il supprimait le tout premier
    // message qu’un nouveau client reçoit de nous. Le libellé doit le dire.
    title: 'Confirmer la commande et demander les photos',
    on: 'Activé. Moins de 20 secondes après la commande, le client reçoit un message qui lui confirme son pack, son format et son adresse de livraison, puis lui demande ses photos. S’il n’envoie rien, il est relancé 24h puis 72h après, et on s’arrête là.',
    off: 'Coupé. Le client ne reçoit rien du tout après sa commande : ni la confirmation (pack, format, adresse), ni les rappels pour les photos. Pour un nouveau client, c’est son tout premier message de notre part qui est supprimé.',
  },
  {
    column: 'ladder_which_album_enabled',
    title: 'Demander « ces photos, c’est pour quel album ? »',
    on: 'Activé. Sur une commande à plusieurs albums, on demande au client de quel album il s’agit.',
    off: 'Coupé. Un humain range les photos dans le bon album.',
  },
  {
    column: 'ladder_cover_enabled',
    title: 'Relancer pour le choix de la couverture',
    on: 'Activé. Le client qui n’a pas choisi sa couverture reçoit un rappel avec son lien.',
    off: 'Coupé. Aucun rappel de couverture n’est envoyé.',
  },
  {
    column: 'ladder_preview_enabled',
    title: 'Relancer pour l’aperçu 3D',
    on: 'Activé. Le client qui n’a pas répondu à son aperçu reçoit un rappel.',
    off: 'Coupé. Aucun rappel d’aperçu n’est envoyé.',
  },
  {
    column: 'ladder_milestones_enabled',
    title: 'Annoncer les étapes (couverture prête, aperçu prêt, paiement reçu)',
    on: 'Activé. Le client est prévenu tout seul quand son album avance. Ces messages ne contiennent ni prix ni RIB.',
    off: 'Coupé. Le client n’est prévenu que si quelqu’un lui écrit.',
  },
  {
    column: 'ladder_payment_enabled',
    title: 'Rappel de paiement (envoie le RIB)',
    sensitive: true,
    on: 'ACTIVÉ. Le client reçoit tout seul le montant à payer et vos coordonnées bancaires — la première demande comme les rappels.',
    off: 'Coupé. Aucun RIB et aucun montant ne part tout seul. C’est vous qui demandez le paiement.',
    confirmOn: 'Laisser l’assistant envoyer le RIB tout seul ?\n\n'
      + 'Le client recevra le montant à payer ET vos coordonnées bancaires, sans relecture. '
      + 'Cela concerne la première demande de paiement comme les rappels.\n\nContinuer ?',
  },
  {
    column: 'ladder_review_enabled',
    title: 'Demander un avis après la livraison (avec les 10 %)',
    on: 'Activé. Un client livré reçoit le lien d’avis et les 10 % sur sa prochaine commande — une seule fois.',
    off: 'Coupé. Aucun avis n’est demandé.',
  },
  {
    column: 'ladder_rules_enabled',
    title: 'Mes relances personnalisées (onglet Relances)',
    on: 'Activé. Les relances que vous avez écrites vous-même partent selon leurs règles.',
    off: 'Coupé. Elles ne partent pas — même celles que vous approuvez à la main dans la file d’attente.',
  },
  {
    column: 'ladder_client_promise_enabled',
    // Migration 157. ⚠️ LA SEULE CASE QUI N’EST PAS COUPÉE AU DÉPART : la
    // fonction tournait déjà en production avant que la case existe, et la
    // livrer coupée l’aurait éteinte au déploiement sans que personne ne le
    // décide. Ce qui va partir est visible AVANT l’envoi dans la carte
    // « Relances au jour choisi par le client » (ClientPromisesCard.tsx).
    //
    // ⚠️ COUPÉE, ELLE LÈVE AUSSI L’ATTENTE. Les autres relances ne patientent
    // plus pour un message qui ne partira jamais — le libellé OFF doit le dire.
    title: 'Relancer le client le jour qu’il a lui-même choisi',
    on: 'Activé. Quand un client annonce un jour (« je confirme lundi »), il reçoit un seul message ce jour-là à partir de 10 h, qui reprend ses propres mots. En attendant, les autres relances ne lui écrivent pas.',
    off: 'Coupé. Aucun message ne part le jour annoncé, et les autres relances continuent normalement, comme si le client n’avait rien annoncé.',
  },
  {
    column: 'ladder_ai_replies_enabled',
    title: 'Laisser l’IA écrire ses propres réponses',
    sensitive: true,
    on: 'ACTIVÉ. Les réponses écrites par l’IA partent sans relecture. Tout message que nous ne savons pas classer part aussi sous ce réglage.',
    off: 'Coupé. L’IA prépare ses réponses, un humain les envoie. Seuls les messages ci-dessus, écrits par nous, partent tout seuls.',
    confirmOn: 'Laisser l’IA répondre toute seule ?\n\n'
      + 'Ses réponses partiront sans que personne ne les relise. Les autres réglages ci-dessus '
      + 'sont des messages écrits par nous, mot pour mot ; celui-ci ne l’est pas.\n\nContinuer ?',
  },
];

/** The number internal alerts go to. Kept beside the toggle that enables them
 *  so switching alerts on cannot leave them pointed at nobody. */
const MANAGER_NUMBER = '212623685162';

function NumberRow({ display, onRemove, onReset, disabled, resetting }: {
  display: string; onRemove: () => void; onReset: () => void;
  disabled: boolean; resetting: boolean;
}) {
  const { t } = useT();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3.5 py-2.5">
      <span className="text-[13px] font-semibold text-gray-800 tabular-nums flex-1">{display}</span>
      {/* Wipes the whole conversation so the next message is the first the
          assistant has ever seen. Destructive and irreversible, so it asks —
          and the worker refuses any number not on this list. */}
      <button
        type="button"
        disabled={disabled || resetting}
        onClick={onReset}
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        {resetting ? t('Réinitialisation…') : t('Réinitialiser la conversation')}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={onRemove}
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-red-600 hover:text-red-800 underline underline-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        <XIcon className="w-3.5 h-3.5" />
        {t('Retirer')}
      </button>
    </div>
  );
}

export function TestNumbersCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const [raw, setRaw] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  /** Null while untouched, so the field shows the saved value until the owner
   *  starts typing. A controlled input seeded from a loading query would
   *  otherwise blank itself the moment the fetch resolves. */
  const [reviewDraft, setReviewDraft] = useState<string | null>(null);
  const [unhappyDraft, setUnhappyDraft] = useState<string | null>(null);
  const [capDraft, setCapDraft] = useState<string | null>(null);
  const [annDraft, setAnnDraft] = useState<string | null>(null);
  const [autoDraft, setAutoDraft] = useState<string | null>(null);
  const [annUntil, setAnnUntil] = useState<string | null>(null);
  const [resumeText, setResumeText] = useState('');
  const [resumeHours, setResumeHours] = useState('3');
  const [resumeResult, setResumeResult] = useState<string | null>(null);
  /** Which journey stage's switches are showing. One at a time — opening a
   *  second would rebuild the list of things on screen, which is the problem
   *  this whole pass exists to fix. */
  const [openStage, setOpenStage] = useState<string | null>(null);

  /** How many people heard the announcement and have not been picked back up.
   *  Drives both the count and whether the button can be pressed at all — the
   *  button is disabled at zero, so it cannot be fired into an empty list. */
  const resumePending = useQuery({
    queryKey: ['resume-pending'],
    queryFn: () => whatsappApi.resumePending(),
  });

  const resume = useMutation({
    mutationFn: (v: { text: string; window_hours: number }) =>
      whatsappApi.resumeAfterIncident(v),
    onSuccess: (r) => {
      // Report what ACTUALLY happened, including who could not be reached.
      // "Sent!" on a run that skipped half the list is how somebody finds out
      // a week later that customers were never told.
      const parts = [`${r.queued} message(s) programmé(s)`];
      if (r.skipped) parts.push(`${r.skipped} sans commande liée — à faire à la main`);
      if (r.last_at) {
        parts.push(`dernier vers ${new Date(r.last_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`);
      }
      if (!r.queued && r.reason) parts.push(r.reason);
      setResumeResult(parts.join(' · '));
      setResumeText('');
      qc.invalidateQueries({ queryKey: ['resume-pending'] });
    },
    onError: (e) => setResumeResult((e as Error).message),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['app-settings', 'agent_test_numbers'],
    queryFn: () => settingsApi.get(),
  });
  const numbers = (data?.settings.agent_test_numbers ?? []) as string[];
  const sendEnabled = (data?.settings as { assistant_send_enabled?: boolean } | undefined)?.assistant_send_enabled === true;

  const settings = (data?.settings ?? {}) as Record<string, unknown>;

  /** ⚠️ Mirrors `activeAnnouncement` in the worker: the switch alone is not
   *  "on". Empty text or a passed deadline are off there, and a card that
   *  showed "EN COURS" for either would be lying about what customers receive. */
  const annText = typeof settings.announcement_text === 'string' ? settings.announcement_text : '';
  const annUntilIso = typeof settings.announcement_until === 'string' ? settings.announcement_until : '';
  const annOn = settings.announcement_enabled === true
    && Boolean(annText.trim())
    && (!annUntilIso || Date.parse(annUntilIso) > Date.now());

  const pullOn = settings.assistant_media_pull_enabled === true;
  const alertsOn = String(settings.manager_whatsapp ?? '').trim().length > 0;
  const createOrdersOn = settings.assistant_create_orders_enabled === true;
  // Owner, 2026-10-08: no 3D preview goes to a client before his OK. Ships OFF.
  const previewApprovalOn = settings.preview_approval_required === true;
  // Owner, 2026-10-10: an order is created only from photos or money. Ships OFF.
  const photosOrMoneyOn = settings.order_needs_photos_or_money === true;
  // Owner, 2026-10-10: the manager alerts rebuild (6 pings, 9 h / 18 h summary). Ships OFF.
  const alertsV2On = settings.owner_alerts_v2 === true;
  // Owner, 2026-10-10 — the confirmation report's fixes. Each ships OFF.
  const newPagePrice5 = Number(settings.price_per_page) === 5;
  const priceBeforeOn = settings.price_before_approval === true;
  const claimGateOn = settings.status_claim_gate === true;
  const waitingOn = settings.waiting_on_us_alerts === true;
  const reviewLinkOn = String(settings.review_link ?? '').trim().length > 0;
  const statusRepliesOn = settings.agent_status_replies_enabled !== false;
  // Owner, 2026-10-23 (migration 266) — both ship OFF; a missing column reads OFF.
  const coverFontOn = settings.agent_cover_font_enabled === true;
  const reframeOn = settings.agent_photo_reframe_enabled === true;
  const pauseOnHumanOn = settings.agent_pause_on_human_reply !== false;
  const autoTexts = Array.isArray(settings.wa_auto_reply_texts) ? settings.wa_auto_reply_texts.filter((x) => typeof x === 'string') : [];
  // Owner, 2026-10-18 — 'off' | 'test' | 'on' (migrations 254/255).
  const modeOf = (k: string): 'off' | 'test' | 'on' => {
    const v = (settings as Record<string, unknown>)[k];
    return v === 'on' || v === 'test' ? v : 'off';
  };
  const takeoverMode = modeOf('takeover_alerts');
  const reactionsMode = modeOf('panel_reactions');
  const deliveryMode = modeOf('delivery_answers');
  const proactiveMode = modeOf('delivery_proactive');
  const dayModes = [takeoverMode, reactionsMode, deliveryMode, proactiveMode];
  const modeHint = (m: 'off' | 'test' | 'on', on: string, off: string) =>
    m === 'on' ? on : m === 'test' ? t('Test : seulement pour les numéros de test ci-dessus.') : off;
  /** `=== true` and nothing else, exactly like the worker reads it: a column
   *  that is missing (migration 111 not applied yet), null, or the string
   *  'true' must draw as OFF. The banner must never claim a protection the
   *  worker is not actually applying — or the reverse. */
  const unrestrictedOn = settings.agent_unrestricted_enabled === true;
  /** Migration 132. Every NEW order adds its customer to the list below.
   *  `=== true` for the same reason as the line above: a missing column must
   *  draw as OFF, never as a protection the worker is not applying. */
  const autoEnrolOn = settings.agent_auto_enroll_new_orders === true;
  /** The three states of one question — who does the assistant answer? They
   *  live in two booleans, but the owner should never have to combine two
   *  switches in his head to know the answer. */
  const audience: 'test' | 'new_orders' | 'all' =
    unrestrictedOn ? 'all' : autoEnrolOn ? 'new_orders' : 'test';
  const ladderOn = (column: string) => settings[column] === true;

  /** One mutation for every operational switch — they all PATCH the same
   *  settings row, and a separate hook per toggle would be three copies of
   *  the same four lines. */
  const oneSetting = useMutation({
    mutationFn: (patch: Record<string, unknown>) => settingsApi.update(patch as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings', 'agent_test_numbers'] }),
  });

  const saveMut = useMutation({
    mutationFn: (next: string[]) => settingsApi.update({ agent_test_numbers: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings', 'agent_test_numbers'] }),
  });

  const sendMut = useMutation({
    mutationFn: (v: boolean) => settingsApi.update({ assistant_send_enabled: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings', 'agent_test_numbers'] }),
  });

  const trimmed = raw.trim();
  const canon = trimmed ? canonicalPhone(trimmed) : null;
  const valid = !!canon
    && (isPlausibleMoroccanMobile(canon) || isPlausibleInternational(trimmed, canon));

  const handleAdd = () => {
    setFormError(null);
    if (!trimmed) return;
    if (!canon || !valid) {
      setFormError(t(
        'Numéro non reconnu. Maroc : 0623685162, 212623685162 ou +212 623 68 51 62. ' +
        'Autre pays : indicatif obligatoire, ex. +971 56 379 7374. Un numéro mal saisi ne ' +
        'correspond à personne : l’assistant resterait silencieux sans que la cause soit visible.'
      ));
      return;
    }
    if (numbers.includes(canon)) {
      setFormError(t('Ce numéro est déjà dans la liste.'));
      return;
    }
    saveMut.mutate([...numbers, canon], { onSuccess: () => { setRaw(''); setFormError(null); } });
  };

  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const resetOne = useMutation({
    mutationFn: (phone: string) => whatsappApi.resetTestNumber(phone),
    onSettled: () => { setResetTarget(null); qc.invalidateQueries(); },
  });

  const handleReset = (n: string) => {
    // It deletes every message, photo and draft for this number and cancels
    // their open orders. Irreversible, so it is confirmed in words that say
    // what actually happens rather than "are you sure?".
    const ok = window.confirm(
      t('Effacer TOUTE la conversation avec ce numéro (messages, photos, brouillons) et annuler ses commandes en cours ?\n\nL\'assistant repartira de zéro. Irréversible.'),
    );
    if (!ok) return;
    setResetTarget(n);
    resetOne.mutate(n);
  };

  const handleRemove = (n: string) => {
    const isLast = numbers.length === 1;
    if (isLast) {
      const confirmed = window.confirm(
        t(
          'Retirer ce numéro videra la liste.\n\n' +
          'Dès que la liste est vide, plus AUCUNE réponse automatique n’est envoyée, à personne. ' +
          'L’assistant continue de préparer des réponses, mais un humain devra les envoyer.\n\n' +
          'Continuer ?'
        )
      );
      if (!confirmed) return;
    }
    saveMut.mutate(numbers.filter((x) => x !== n));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start gap-2.5 mb-1">
        <ShieldIcon className="w-[18px] h-[18px] text-gray-400 mt-0.5" />
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{t('Envoi automatique')}</h2>
          <p className="text-[11.5px] text-gray-500 mt-0.5 leading-snug max-w-2xl">
            {t(
              'Pour qu’un message parte tout seul, il faut les QUATRE : préparer les réponses (onglet ' +
              'Cerveau), envoyer les réponses (ci-dessous), la case du type de message concerné, et le ' +
              'numéro du client dans cette liste. Si un seul manque, le message est préparé mais rien ' +
              'n’est envoyé.'
            )}
          </p>
        </div>
      </div>

      {/* ── Switch 2 of 3: actually send. Separate from the Cerveau toggle so
          the assistant can draft for days while sending nothing. ── */}
      <div className={`mt-3.5 rounded-xl border p-4 ${sendEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={sendEnabled} className="mt-0.5 accent-brand-600 w-4 h-4"
            disabled={sendMut.isPending || isLoading}
            onChange={(e) => {
              // Confirm only on the way ON. Switching sending OFF is always
              // safe and must never be slowed down by a dialog.
              if (e.target.checked && !window.confirm(t(
                'Activer l’envoi automatique ?\n\n' +
                'L’assistant enverra ses réponses tout seul, sans relecture, aux numéros de la liste ' +
                'ci-dessous uniquement.\n\n' +
                'Les réponses déjà préparées pendant que l’envoi était coupé ne seront PAS envoyées — ' +
                'seules les nouvelles conversations le seront.'
              ))) return;
              sendMut.mutate(e.target.checked);
            }}
          />
          <span>
            <span className="block text-[13px] font-semibold text-gray-800">
              {t('Envoyer les réponses automatiquement')}
            </span>
            <span className="block text-[11.5px] text-gray-600 mt-0.5 leading-snug">
              {sendEnabled
                ? t('Activé. Les réponses partent seules, uniquement vers les numéros ci-dessous. Maximum 5 messages par 24 h (protection anti-blocage WhatsApp).')
                : t('Coupé. L’assistant prépare les réponses et un humain les envoie lui-même. Rien ne part tout seul.')}
            </span>
          </span>
        </label>
      </div>

      {/* The status banner is the whole point of this card: it must be
          impossible to glance at and misread. Red+unrestricted = everybody.
          Amber+scoped = a few people can receive an automatic answer.
          Neutral+empty = nobody can.

          ⚠️ THE "NOBODY" WORDING IS ONLY HONEST BECAUSE OF THE 2026-08-18 FIX.
          An empty list used to mean "everybody" to the order-journey queue
          while this banner said "personne", so the one state an owner would
          reach for to stop the agent was the one the banner described wrongly.
          The unrestricted branch below exists so that if the real "send to
          everybody" switch is ever turned on in the database, this card says
          so in red instead of continuing to claim the list is what matters. */}
      <div className="mt-3.5">
        {isLoading ? (
          <p className="text-[12px] text-gray-400">{t('Chargement…')}</p>
        ) : unrestrictedOn ? (
          <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2.5">
            <GlobeIcon className="w-[18px] h-[18px] text-red-700 mt-px" />
            <p className="text-[12.5px] text-red-800">
              <span className="font-semibold">{t('Envoi à TOUS les clients.')}</span>{' '}
              {t('La restriction aux numéros de test est levée (réglage agent_unrestricted_enabled). La liste ci-dessous ne protège plus personne tant qu’il est actif.')}
            </p>
          </div>
        ) : numbers.length > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
            <ShieldIcon className="w-[18px] h-[18px] text-amber-700 mt-px" />
            <p className="text-[12.5px] text-amber-800">
              <span className="font-semibold">
                {t('Mode test actif — {n} numéro(s).', { n: String(numbers.length) })}
              </span>{' '}
              {t('Seuls ces numéros peuvent recevoir une réponse automatique. Pour tous les autres clients, l’assistant prépare une réponse et attend un humain.')}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 flex items-start gap-2.5">
            <GlobeIcon className="w-[18px] h-[18px] text-gray-500 mt-px" />
            <p className="text-[12.5px] text-gray-700">
              <span className="font-semibold">{t('Aucun numéro de test.')}</span>{' '}
              {t('Personne ne reçoit de message automatique : ni réponse, ni rappel, ni message de suivi de commande. L’assistant continue de préparer des réponses pour relecture.')}
            </p>
          </div>
        )}
      </div>

      {isError && (
        <p className="text-[11.5px] text-red-600 mt-3" role="alert">{t('Impossible de charger les numéros de test.')}</p>
      )}

      {/* ── Owner, 2026-10-18: the four switches of the day — takeover pings,
          reactions, delivery answers, delivery updates. Each is 'off' | 'test'
          | 'on' in app_settings (migrations 254/255); a toggle writes 'on' or
          'off' (« test » = only the numbers above, set by hand). ── */}
      <SettingGroup
        title={t('Reprise de conversation et suivi des colis')}
        summary={`${dayModes.filter((m) => m === 'on').length} sur 4 activé(s)`}
        tone={'default'}
        loading={isLoading}
      >
      <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
        <Toggle
          checked={takeoverMode === 'on'}
          onChange={(v) => oneSetting.mutate({ takeover_alerts: v ? 'on' : 'off' })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Me prévenir par notification quand un client écrit pendant une reprise')}
          hint={modeHint(takeoverMode, t('Activé. Quand quelqu’un a « Pris la main », il reçoit une notification sur SES appareils (une seule par conversation, mise à jour : « Sara #1248 — 3 nouveaux messages ») — sauf s’il a la conversation ouverte. Plus de message WhatsApp. S’arrête quand l’assistant reprend. Chacun active les notifications sur son téléphone dans Notifications.'), t('Coupé. Pendant une reprise, personne n’est prévenu.'))}
        />
        <Toggle
          checked={reactionsMode === 'on'}
          onChange={(v) => oneSetting.mutate({ panel_reactions: v ? 'on' : 'off' })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Réactions et emojis dans les conversations')}
          hint={modeHint(reactionsMode, t('Activé. ❤️ 👍 🙏 sur un message du client (vraie réaction WhatsApp), ses réactions visibles, bouton emoji. Une réaction ne compte jamais comme une réponse.'), t('Coupé. Pas de réactions ni d’emojis dans le panneau.'))}
        />
        <Toggle
          checked={deliveryMode === 'on'}
          onChange={(v) => oneSetting.mutate({ delivery_answers: v ? 'on' : 'off' })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Répondre « où est ma commande ? » avec les vraies données AMEEX')}
          hint={modeHint(deliveryMode, t('Activé. L’assistant répond avec vos 5 phrases (impression, remis à AMEEX, en route, arrive aujourd’hui, livré). Un problème (retard, retour, échec) : il ne répond pas, vous êtes prévenu.'), t('Coupé. Comme avant.'))}
        />
        <Toggle
          checked={proactiveMode === 'on'}
          onChange={(v) => oneSetting.mutate({ delivery_proactive: v ? 'on' : 'off' })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Prévenir le client à chaque étape (imprimé, remis à AMEEX, en livraison)')}
          hint={modeHint(proactiveMode, t('Activé. Un message court, une seule fois par étape, jamais la nuit.'), t('Coupé. Le client n’est prévenu que s’il demande.'))}
        />
      </div>
      </SettingGroup>

      <SettingGroup
        title={t('Comportement de l’assistant')}
        summary={`${[pullOn, alertsOn, createOrdersOn, statusRepliesOn, pauseOnHumanOn, previewApprovalOn].filter(Boolean).length} réglage(s) sur 6 activé(s)${previewApprovalOn ? ' · aperçus validés par vous' : ''}`}
        tone={'default'}
        loading={isLoading}
      >
      {/* ── Operational switches. Here rather than in Cerveau because each one
          governs an ACTION the assistant takes on its own, and they are the
          dials that get reached for when something is going wrong. ── */}
      <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
        <Toggle
          checked={pullOn}
          onChange={(v) => oneSetting.mutate({ assistant_media_pull_enabled: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Récupérer les photos des clients')}
          hint={pullOn
            ? t('Activé. Les photos envoyées en document sont téléchargées et rangées automatiquement.')
            : t('Coupé. Les photos restent chez WhatsApp pendant 30 jours et ne sont pas récupérées.')}
        />
        <Toggle
          checked={alertsOn}
          onChange={(v) => oneSetting.mutate({ manager_whatsapp: v ? MANAGER_NUMBER : null })}
          disabled={oneSetting.isPending || isLoading}
          title={t('M’alerter sur WhatsApp si un fichier client n’arrive pas')}
          hint={alertsOn
            ? t('Activé. Un seul message par client, même s’il envoie dix fichiers.')
            : t('Coupé. Vous ne serez pas prévenu si le fichier d’un client n’arrive pas.')}
        />
        <Toggle
          checked={createOrdersOn}
          onChange={(v) => {
            if (v && !window.confirm(t(
              'Laisser l’assistant créer des commandes ?\n\n'
              + 'Il posera les questions au client et créera la commande tout seul, '
              + 'sans relecture. Une commande part en production.\n\nContinuer ?'
            ))) return;
            oneSetting.mutate({ assistant_create_orders_enabled: v });
          }}
          disabled={oneSetting.isPending || isLoading}
          title={t('Laisser l’assistant créer des commandes')}
          hint={createOrdersOn
            ? t('Activé. Une commande créée par l’assistant part en production comme une autre.')
            : t('Coupé. L’assistant rassemble les infos, un humain crée la commande.')}
        />
        <Toggle
          checked={previewApprovalOn}
          onChange={(v) => oneSetting.mutate({ preview_approval_required: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Approuver chaque aperçu 3D avant qu’il parte')}
          hint={previewApprovalOn
            ? t('Activé. Chaque aperçu est d’abord contrôlé (ordre d’envoi, visages, consignes), puis vous recevez un WhatsApp : répondez OK pour l’envoyer — ou « Approuver et envoyer » dans la boîte de réception ou sur la commande.')
            : t('Coupé. L’aperçu part chez la cliente dès qu’il est prêt, comme avant.')}
        />
        <Toggle
          checked={photosOrMoneyOn}
          onChange={(v) => oneSetting.mutate({ order_needs_photos_or_money: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Créer une commande seulement avec des photos ou un paiement')}
          hint={photosOrMoneyOn
            ? t('Activé. Une commande naît quand le client envoie ses photos (un lot) ou de l’argent. Avant : tout ce qu’il dit reste sur la conversation et remplit la commande ensuite. Le lien des couvertures arrive avec la commande.')
            : t('Coupé. Comme avant : « je veux commander », un pack ou une demande de lien peuvent aussi créer une commande.')}
        />
        <Toggle
          checked={alertsV2On}
          onChange={(v) => oneSetting.mutate({ owner_alerts_v2: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Alertes : seulement ce qui demande une action')}
          hint={alertsV2On
            ? t('Activé. WhatsApp seulement pour 6 cas (paiement, aperçu, cliente qui attend, annulation ou plainte, WhatsApp coupé, assistant arrêté). Le reste : un résumé à 9 h et 18 h, ou le panneau seulement. Une alerte par commande et par problème, fermée toute seule quand c’est réglé.')
            : t('Coupé. Comme avant : chaque alerte part sur WhatsApp, au plus 6 par jour et par type.')}
        />
        <Toggle
          checked={newPagePrice5}
          onChange={(v) => oneSetting.mutate({ price_per_page: v ? 5 : 8 })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Page supplémentaire à 5 DH (nouvelles commandes)')}
          hint={newPagePrice5
            ? t('Activé. Chaque nouvelle commande garde 5 DH par page supplémentaire. Les anciennes commandes gardent leur prix (8 DH).')
            : t('Coupé. Les nouvelles commandes gardent 8 DH par page supplémentaire.')}
        />
        <Toggle
          checked={priceBeforeOn}
          onChange={(v) => oneSetting.mutate({ price_before_approval: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Annoncer le prix avec l’aperçu')}
          hint={priceBeforeOn
            ? t('Activé. Au-delà de 36 pages, l’aperçu part avec le total calculé par le code, et propose 36 pages au prix du pack. Le total qu’elle accepte est gardé : demande d’avance et colis bloqués s’il ne correspond plus.')
            : t('Coupé. L’aperçu part sans le prix, comme avant.')}
        />
        <Toggle
          checked={claimGateOn}
          onChange={(v) => oneSetting.mutate({ status_claim_gate: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Bloquer les messages faux sur les photos et l’aperçu')}
          hint={claimGateOn
            ? t('Activé. « Photos reçues », « on attend vos photos », « votre album est prêt / voici l’aperçu » et « une personne va… » sont vérifiés avant de partir. Faux : rien ne part et vous êtes prévenu.')
            : t('Coupé. Comme avant.')}
        />
        <Toggle
          checked={waitingOn}
          onChange={(v) => oneSetting.mutate({ waiting_on_us_alerts: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Alerter quand une cliente nous attend')}
          hint={waitingOn
            ? t('Activé. Photos reçues sans aperçu après 24 h : une alerte. Une cliente passée à une personne sans réponse après 3 h : une alerte plus forte.')
            : t('Coupé. Comme avant : relance après 6 h et 24 h.')}
        />
        <Toggle
          checked={statusRepliesOn}
          onChange={(v) => oneSetting.mutate({ agent_status_replies_enabled: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Répondre à « où en est ma commande ? »')}
          hint={statusRepliesOn
            ? t('Activé. Réponse volontairement large : en préparation / en livraison / livrée. Jamais de date, jamais d’étape interne.')
            : t('Coupé. L’assistant dit qu’un humain va vérifier.')}
        />
        <Toggle
          checked={coverFontOn}
          onChange={(v) => oneSetting.mutate({ agent_cover_font_enabled: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('L’agent change la police de couverture')}
          hint={coverFontOn
            ? t('Activé. Si la cliente veut une autre écriture pour le titre, l’agent envoie l’image « Polices couverture » (8 polices numérotées) et applique le numéro choisi. Sans cette image dans le Cerveau, la demande vient chez vous.')
            : t('Coupé. Comme avant : une demande de police vient chez vous.')}
        />
        <Toggle
          checked={reframeOn}
          onChange={(v) => oneSetting.mutate({ agent_photo_reframe_enabled: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('L’agent recadre les photos coupées (visages)')}
          hint={reframeOn
            ? t('Activé. « Les photos sont coupées » : l’agent fait glisser les photos dans leur case pour que les visages soient dans le cadre, puis renvoie l’aperçu. Jamais de photo réduite, de bande blanche, de page ou de prix changé. Rien à corriger : la demande vient chez vous.')
            : t('Coupé. Comme avant : la plainte vient chez vous.')}
        />
        <Toggle
          checked={pauseOnHumanOn}
          onChange={(v) => oneSetting.mutate({ agent_pause_on_human_reply: v })}
          disabled={oneSetting.isPending || isLoading}
          title={t('Se taire quand un humain répond au client')}
          hint={pauseOnHumanOn
            ? t('Activé — mais inactif tant que la passerelle WhatsApp ne renvoie pas les messages sortants. En attendant, utilisez le bouton « Pause » sur la conversation.')
            : t('Coupé. Seul le bouton « Pause » d’une conversation met l’assistant en veille.')}
        />
      </div>

      {/* The phone's own automatic messages (migration 259, 2026-10-21): the
          WhatsApp Business greeting paused the agent on 48 new chats in a week
          as « Humain ». A text listed here is « Message auto »: no pause, and it
          never counts as an answer. */}
      <div className="mt-4 border-t border-gray-100 pt-4">
        <label htmlFor="auto-texts" className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Messages automatiques de WhatsApp Business (accueil / absence)')}
        </label>
        <textarea
          id="auto-texts" rows={3}
          className={`${inputCls} leading-snug`}
          placeholder="Mrhba bik f storiz ina album bghiti?"
          value={autoDraft ?? autoTexts.join('\n')}
          onChange={(e) => setAutoDraft(e.target.value)}
        />
        <div className="flex gap-2 items-center mt-2">
          <Button
            size="sm"
            loading={oneSetting.isPending}
            disabled={autoDraft === null || autoDraft === autoTexts.join('\n')}
            onClick={() => {
              const list = [...new Set((autoDraft ?? '').split('\n').map((x) => x.trim()).filter(Boolean))];
              oneSetting.mutate({ wa_auto_reply_texts: list });
              setAutoDraft(null);
            }}
          >
            {t('Enregistrer')}
          </Button>
        </div>
        <p className="text-[11.5px] text-gray-500 mt-1.5">
          {t('Un texte par ligne, exactement comme dans l’app WhatsApp Business. Un message envoyé par le téléphone avec un de ces textes est marqué « Message auto » : l’assistant reste actif et la cliente reçoit quand même sa réponse.')}
        </p>
      </div>

      </SettingGroup>

      <SettingGroup
        title={t('Ce que l’assistant envoie tout seul')}
        summary={`${LADDER_ROWS.filter((r) => ladderOn(r.column)).length} type(s) sur ${LADDER_ROWS.length} · ${ladderOn('ladder_payment_enabled') ? 'RIB actif' : 'RIB coupé'} · ${ladderOn('ladder_ai_replies_enabled') ? 'IA active' : 'IA coupée'}`}
        tone={(ladderOn('ladder_payment_enabled') || ladderOn('ladder_ai_replies_enabled')) ? 'sensitive' : 'default'}
        loading={isLoading}
      >
      {/* ── WHAT the assistant may send, one switch per famille de message
          (migration 111). Separate from the switches above because those
          govern how the assistant BEHAVES; these govern what a customer
          actually receives. All ship OFF: before them, adding a number to the
          list below switched on fourteen senders at once — including the RIB
          and the model's own free text. ── */}
      <div className="mt-4 border-t border-gray-100 pt-4">
        <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
          {t('Ce que l’assistant peut envoyer tout seul')}
        </h3>
        <p className="text-[11.5px] text-gray-500 mt-1 leading-snug max-w-2xl">
          {t(
            'Chaque case = un type de message. Tout est coupé au départ : cochez seulement ce que vous ' +
            'voulez laisser partir. Ces cases s’ajoutent aux réglages du haut — un message ne part que si ' +
            'sa case est cochée ET que le numéro du client est dans la liste plus bas.'
          )}
        </p>
        {/* ── THE JOURNEY, AS A JOURNEY ────────────────────────────────────
            Five cards in the order a customer meets them. The GAP is the
            information: one green card followed by four grey ones says the
            flow stops after the first message, which a vertical checkbox list
            could never show. Click a card to reach its switches. ── */}
        <div className="mt-3 flex flex-wrap gap-2">
          {JOURNEY.map((stage) => {
            const on = stage.columns.some((c) => ladderOn(c));
            const partial = on && !stage.columns.every((c) => ladderOn(c));
            const sensitive = stage.columns.some(
              (c) => LADDER_ROWS.find((r) => r.column === c)?.sensitive,
            );
            const selected = openStage === stage.key;
            return (
              <button
                key={stage.key}
                type="button"
                aria-pressed={selected}
                onClick={() => setOpenStage(selected ? null : stage.key)}
                className={'flex-1 min-w-[104px] rounded-lg border px-3 py-2.5 text-left '
                  + 'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 '
                  + (selected ? 'ring-2 ring-brand-500 ' : '')
                  + (on ? 'border-green-300 bg-green-50 hover:bg-green-100'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100')}
              >
                <span className="flex items-center gap-1.5">
                  {/* ⚠️ Never colour alone — the dot has a written state beside
                      it, so the card is readable in greyscale too. */}
                  <span className={'w-1.5 h-1.5 rounded-full shrink-0 '
                    + (on ? 'bg-green-600' : 'bg-gray-400')} />
                  <span className="text-[12px] font-semibold text-gray-800 truncate">
                    {t(stage.label)}
                  </span>
                </span>
                <span className={'block text-[10.5px] mt-0.5 '
                  + (on ? 'text-green-700' : 'text-gray-500')}>
                  {on ? (partial ? t('en partie') : t('activé')) : t('coupé')}
                  {sensitive ? ' · RIB' : ''}
                </span>
              </button>
            );
          })}
        </div>

        {/* The switches for whichever stage is open. Below the strip rather
            than inside a card, so a long description cannot make the row of
            cards jump around while you read it. */}
        {openStage && (
          <div className="mt-2.5 space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3">
            {LADDER_ROWS
              .filter((r) => JOURNEY.find((j) => j.key === openStage)?.columns.includes(r.column))
              .map((row) => (
                <Toggle
                  key={row.column}
                  checked={ladderOn(row.column)}
                  sensitive={row.sensitive}
                  disabled={oneSetting.isPending || isLoading}
                  onChange={(v) => {
                    if (v && row.confirmOn && !window.confirm(t(row.confirmOn))) return;
                    oneSetting.mutate({ [row.column]: v });
                  }}
                  title={t(row.title)}
                  hint={t(ladderOn(row.column) ? row.on : row.off)}
                />
              ))}
          </div>
        )}

        {/* ⚠️ NOT STAGES — see the note on JOURNEY. These three happen
            alongside the journey rather than inside it, so they keep the plain
            row treatment instead of being forced into a card that would draw a
            step that does not exist. */}
        <div className="mt-3.5 space-y-2.5 border-t border-gray-100 pt-3.5">
          {LADDER_ROWS.filter((r) => OFF_JOURNEY.includes(r.column)).map((row) => (
            <Toggle
              key={row.column}
              checked={ladderOn(row.column)}
              sensitive={row.sensitive}
              disabled={oneSetting.isPending || isLoading}
              onChange={(v) => {
                if (v && row.confirmOn && !window.confirm(t(row.confirmOn))) return;
                oneSetting.mutate({ [row.column]: v });
              }}
              title={t(row.title)}
              hint={t(ladderOn(row.column) ? row.on : row.off)}
            />
          ))}
        </div>
      </div>

      </SettingGroup>

      <SettingGroup
        title={t('Annonce exceptionnelle')}
        summary={annOn ? t('EN COURS — recopiée à chaque client') : t('Coupée')}
        tone={annOn ? 'sensitive' : 'default'}
        loading={isLoading}
      >
      {/* ── ANNOUNCEMENT (migration 114) ──────────────────────────────────
          The incident banner. Sits ABOVE everything else in this card because
          when it is on, it is the most important thing the assistant is doing —
          and because a control you only reach for during a crisis must be the
          first one you see, not the seventh. ── */}
      <div className={`mt-4 border-t pt-4 ${annOn ? 'border-amber-300' : 'border-gray-100'}`}>
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor="ann-text" className="block text-[11px] font-semibold text-gray-500">
            {t('Annonce exceptionnelle')}
          </label>
          <label className="flex items-center gap-1.5 text-[11.5px] text-gray-700 shrink-0">
            <input
              type="checkbox" checked={annOn}
              disabled={oneSetting.isPending || isLoading}
              onChange={(e) => {
                if (e.target.checked && !annText.trim()) {
                  window.alert(t('Écrivez d’abord le message, puis activez.'));
                  return;
                }
                oneSetting.mutate({ announcement_enabled: e.target.checked });
              }}
              className="rounded border-gray-300"
            />
            {annOn ? t('EN COURS') : t('Coupée')}
          </label>
        </div>

        <textarea
          id="ann-text" rows={3}
          className={`${inputCls} mt-1.5 leading-snug`}
          placeholder={t('Ex : Suite à un déménagement, les livraisons reprennent lundi 25. Vos commandes sont bien enregistrées.')}
          value={annDraft ?? annText}
          onChange={(e) => setAnnDraft(e.target.value)}
        />

        <div className="flex gap-2 items-end mt-2">
          <div>
            <label htmlFor="ann-until" className="block text-[11px] text-gray-500 mb-1">
              {t('S’arrête automatiquement le')}
            </label>
            <input
              id="ann-until" type="date" className={`${inputCls} max-w-[170px]`}
              value={annUntil ?? annUntilIso.slice(0, 10)}
              onChange={(e) => setAnnUntil(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            loading={oneSetting.isPending}
            disabled={annDraft === null && annUntil === null}
            onClick={() => {
              const text = (annDraft ?? annText).trim();
              const day = (annUntil ?? annUntilIso.slice(0, 10)).trim();
              oneSetting.mutate({
                announcement_text: text || null,
                // End of the chosen DAY, not its first second — picking today
                // should mean "until tonight", not "already expired".
                announcement_until: day ? new Date(`${day}T23:59:59`).toISOString() : null,
              });
              setAnnDraft(null); setAnnUntil(null);
            }}
          >
            {t('Enregistrer')}
          </Button>
        </div>

        <p className="text-[11.5px] text-gray-500 mt-1.5 leading-snug">
          {t(
            'Quand elle est en cours, l’assistant recopie ce message MOT POUR MOT à chaque client — '
            + 'en plus de sa réponse normale, jamais à la place. Utile en cas de panne, de déménagement '
            + 'ou de commandes en pause : les clients sont prévenus au lieu d’être ignorés.'
          )}
        </p>
        {annOn && !annUntilIso && (
          <p className="text-[11.5px] text-amber-700 mt-1.5">
            {t('⚠️ Aucune date de fin : cette annonce partira à tous les clients jusqu’à ce que vous la coupiez à la main.')}
          </p>
        )}
      </div>

      {/* ── RESUME AFTER THE INCIDENT (migration 115) ─────────────────────
          Directly under the announcement, because it is the other half of it:
          one tells everybody to wait, this one tells them to stop waiting.
          Without it the announcement is a one-way door. ── */}
      </SettingGroup>

      <SettingGroup
        title={t('Reprendre après un incident')}
        summary={`${resumePending.data?.pending ?? 0} client(s) en attente de reprise`}
        tone={'default'}
        loading={isLoading}
      >
      <div className="mt-4 border-t border-gray-100 pt-4">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor="resume-text" className="block text-[11px] font-semibold text-gray-500">
            {t('Reprendre les conversations')}
          </label>
          <span className="text-[11px] text-gray-400 tabular-nums shrink-0">
            {resumePending.data
              ? t('{n} client(s) en attente').replace('{n}', String(resumePending.data.pending))
              : ''}
          </span>
        </div>

        <textarea
          id="resume-text" rows={2}
          className={`${inputCls} mt-1.5 leading-snug`}
          placeholder={t('Ex : Bonne nouvelle, nous avons repris ! Merci de votre patience.')}
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
        />

        <div className="flex gap-2 items-end mt-2">
          <div>
            <label htmlFor="resume-hours" className="block text-[11px] text-gray-500 mb-1">
              {t('Étalé sur (heures)')}
            </label>
            <input
              id="resume-hours" type="number" min={1} max={24}
              className={`${inputCls} max-w-[90px]`}
              value={resumeHours}
              onChange={(e) => setResumeHours(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            loading={resume.isPending}
            disabled={!resumeText.trim() || !(resumePending.data?.pending) || resume.isPending}
            onClick={() => {
              const n = resumePending.data?.pending ?? 0;
              const h = Math.max(1, Math.min(24, parseInt(resumeHours, 10) || 3));
              // ⚠️ CONFIRM, ALWAYS. This is the only control on this page that
              // messages many real customers in one press, and it cannot be
              // taken back once the rows are queued.
              if (!window.confirm(t(
                'Envoyer ce message à {n} client(s), réparti au hasard sur {h} h ?\n\n'
                + 'Chacun recevra aussi la prochaine étape de SA commande. '
                + 'Impossible d’annuler une fois lancé.',
              ).replace('{n}', String(n)).replace('{h}', String(h)))) return;
              resume.mutate({ text: resumeText.trim(), window_hours: h });
            }}
          >
            {t('Lancer la reprise')}
          </Button>
        </div>

        <p className="text-[11.5px] text-gray-500 mt-1.5 leading-snug">
          {t(
            'Écrit uniquement aux clients qui ont REÇU l’annonce. Chacun reçoit votre message '
            + 'plus la prochaine étape de sa commande (photos, couverture, aperçu ou paiement). '
            + 'Les envois sont répartis au hasard sur la durée choisie — jamais tous en même temps, '
            + 'pour éviter de faire bannir le numéro.'
          )}
        </p>
        {resumeResult && (
          <p className="text-[11.5px] text-gray-700 mt-1.5">{resumeResult}</p>
        )}
      </div>

      {/* ── How many PEOPLE the assistant may write to per day (migration 112).
          Sits directly under the "what can it send" checkboxes because it is
          the other half of the same question: those decide WHAT goes out, this
          decides TO HOW MANY. Was a hardcoded 5 until 2026-08-19. ── */}
      </SettingGroup>

      <SettingGroup
        title={t('Nombre de clients par jour')}
        summary={`${String(settings.agent_daily_chat_cap ?? 5)} client(s) différent(s) par 24 h`}
        tone={'default'}
        loading={isLoading}
      >
      <div className="mt-4 border-t border-gray-100 pt-4">
        <label htmlFor="daily-cap" className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Nombre de clients par jour')}
        </label>
        <div className="flex gap-2 items-start">
          <input
            id="daily-cap" className={`${inputCls} max-w-[120px]`} type="number" min={0} max={200}
            value={capDraft ?? String(settings.agent_daily_chat_cap ?? 5)}
            onChange={(e) => setCapDraft(e.target.value)}
          />
          <Button
            size="sm"
            loading={oneSetting.isPending}
            disabled={capDraft === null || capDraft === String(settings.agent_daily_chat_cap ?? 5)}
            onClick={() => {
              const n = Math.max(0, Math.min(200, parseInt(capDraft ?? '', 10) || 0));
              // Confirm only upward, and only past the cold-start budget — the
              // same rule as the sensitive toggles above. Lowering it is always
              // safe and must never be slowed down.
              const current = Number(settings.agent_daily_chat_cap ?? 5);
              if (n > current && n > 5 && !window.confirm(t(
                'Augmenter ce nombre trop vite peut faire bannir le numéro WhatsApp — et un numéro banni '
                + 'emporte toute la ligne de support, y compris les clients en pleine conversation. '
                + 'Montez progressivement et surveillez les échecs d’envoi. Continuer ?'
              ))) return;
              oneSetting.mutate({ agent_daily_chat_cap: n });
              setCapDraft(null);
            }}
          >
            {t('Enregistrer')}
          </Button>
        </div>
        <p className="text-[11.5px] text-gray-500 mt-1.5">
          {t(
            'Combien de CLIENTS DIFFÉRENTS l’assistant peut contacter DE LUI-MÊME (relances, liens, rappels) '
            + 'par tranche de 24 h — pas combien de messages. Un client qui reçoit six messages dans la journée '
            + 'compte pour un seul. Les autres ne sont pas perdus : ils attendent qu’une place se libère. '
            + 'Une réponse à un client qui vient d’écrire ne compte pas et n’attend jamais.'
          )}
        </p>
        <p className="text-[11.5px] text-amber-700 mt-1.5">
          {t(
            '5 au départ, parce qu’un numéro WhatsApp tout neuf qui se met à écrire à beaucoup d’inconnus '
            + 'se fait bannir. Pour de gros volumes, c’est l’API WhatsApp Business qu’il faut, pas ce réglage.'
          )}
        </p>
      </div>

      {/* ── Compensation after a complaint. A number, so it lives here rather
          than in the Brain: the assistant is forbidden from inventing it and
          reads this exact figure. ── */}
      </SettingGroup>

      <SettingGroup
        title={t('Geste commercial après une réclamation')}
        summary={`${String(settings.agent_unhappy_discount_pct ?? 20)} % sur la commande suivante`}
        tone={'default'}
        loading={isLoading}
      >
      <div className="mt-4 border-t border-gray-100 pt-4">
        <label htmlFor="unhappy-pct" className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Geste commercial après une réclamation (%)')}
        </label>
        <div className="flex gap-2 items-start">
          <input
            id="unhappy-pct" className={`${inputCls} max-w-[120px]`} type="number" min={0} max={100}
            value={unhappyDraft ?? String(settings.agent_unhappy_discount_pct ?? 20)}
            onChange={(e) => setUnhappyDraft(e.target.value)}
          />
          <Button
            size="sm"
            loading={oneSetting.isPending}
            disabled={unhappyDraft === null
              || unhappyDraft === String(settings.agent_unhappy_discount_pct ?? 20)}
            onClick={() => {
              const n = Math.max(0, Math.min(100, parseInt(unhappyDraft ?? '', 10) || 0));
              oneSetting.mutate({ agent_unhappy_discount_pct: n });
              setUnhappyDraft(null);
            }}
          >
            {t('Enregistrer')}
          </Button>
        </div>
        <p className="text-[11.5px] text-gray-500 mt-1.5">
          {t('Proposé UNIQUEMENT sur la prochaine commande, et seulement après que le client ait envoyé une photo du problème. Jamais sur la commande en cours — un geste sur celle-ci est un remboursement, et c’est vous qui décidez.')}
        </p>
      </div>

      {/* ── The review link. EMPTY IS THE OFF SWITCH, and it is empty by
          default: the worker sends nothing at all until a real URL is here,
          exactly as it refuses to send a payment message without a RIB. So
          this field is both the setting and the feature's on/off. ── */}
      </SettingGroup>

      <SettingGroup
        title={t('Lien d’avis Google')}
        summary={reviewLinkOn ? t('Configuré — la demande d’avis peut partir') : t('Vide — aucune demande d’avis ne part')}
        tone={'default'}
        loading={isLoading}
      >
      <div className="mt-4 border-t border-gray-100 pt-4">
        <label htmlFor="review-link" className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Lien d’avis (Google)')}
        </label>
        <div className="flex gap-2 items-start">
          <input
            id="review-link" className={inputCls} type="url" inputMode="url"
            value={reviewDraft ?? String(settings.review_link ?? '')}
            onChange={(e) => setReviewDraft(e.target.value)}
            placeholder="https://g.page/r/…/review"
          />
          <Button
            size="sm"
            loading={oneSetting.isPending}
            disabled={reviewDraft === null || reviewDraft === String(settings.review_link ?? '')}
            onClick={() => {
              const v = (reviewDraft ?? '').trim();
              oneSetting.mutate({ review_link: v || null });
              setReviewDraft(null);
            }}
          >
            {t('Enregistrer')}
          </Button>
        </div>
        <p className="text-[11.5px] text-gray-500 mt-1.5">
          {reviewLinkOn
            ? t('Activé. Un client dont l’album est livré reçoit ce lien et les 10% sur sa prochaine commande — une seule fois, jamais deux.')
            : t('Vide = coupé. Tant qu’il n’y a pas de lien ici, l’assistant ne demande aucun avis et ne parle d’aucune remise.')}
        </p>
      </div>

      </SettingGroup>

      {/* ── Switch 3 of 3: WHO. ──────────────────────────────────────────
          A dropdown rather than a toggle, deliberately. "On" said nothing
          about who it was on FOR: the owner read the assistant as switched on
          and it was answering two numbers. Both states are now named, so the
          screen cannot be read as more or less live than it is.

          It writes `agent_unrestricted_enabled` — the SAME column the
          confirmation outbox already obeys (migration 111). One switch, both
          paths: two would eventually disagree, and the customer would be told
          the assistant was off while half of it kept writing. ── */}
      <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-4">
        {t('Qui reçoit les messages')}
      </h3>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <select
          aria-label={t('Qui reçoit les messages')}
          className={`${inputCls} max-w-[280px]`}
          value={audience}
          disabled={isLoading || oneSetting.isPending}
          onChange={(e) => {
            const next = e.target.value as 'test' | 'new_orders' | 'all';
            // ⚠️ CONFIRM ONLY ON THE WAY OUT OF THE SANDBOX. Narrowing is always
            // safe and must never be slowed down; widening points a language
            // model at people who have not been checked.
            if (next === 'all' && !window.confirm(t(
              'L’assistant va répondre à TOUS les clients, pas seulement aux numéros de test. '
              + 'Chaque message part sans relecture. Continuer ?'
            ))) return;
            if (next === 'new_orders' && !window.confirm(t(
              'Chaque NOUVELLE commande sera ajoutée automatiquement à la liste, et l’assistant '
              + 'répondra à ce client sans relecture. Les clients déjà dans la liste ne changent pas. '
              + 'Continuer ?'
            ))) return;
            // ⚠️ BOTH COLUMNS, EVERY TIME. Writing only the one that changed
            // leaves the other set from a previous choice — pick « Tous les
            // clients » then « Numéros de test » and auto-enrolment would still
            // be quietly on, which is exactly the "I thought it was off" bug
            // this dropdown exists to prevent.
            oneSetting.mutate({
              agent_unrestricted_enabled: next === 'all',
              agent_auto_enroll_new_orders: next === 'new_orders',
            });
          }}
        >
          <option value="test">{t('Numéros de test seulement')}</option>
          <option value="new_orders">{t('Numéros de test + chaque nouvelle commande')}</option>
          <option value="all">{t('Tous les clients')}</option>
        </select>
        {oneSetting.isPending && (
          <span className="text-[11.5px] text-gray-500">{t('Enregistrement…')}</span>
        )}
      </div>

      <p className="text-[11.5px] text-gray-500 mt-1.5">
        {audience === 'all'
          ? t('Tous les clients reçoivent les réponses automatiques. La liste ci-dessous ne protège plus personne.')
          : audience === 'new_orders'
            ? t('Chaque nouvelle commande est ajoutée à la liste ci-dessous automatiquement. Les clients qui n’ont jamais commandé ne reçoivent rien. Revenir sur « Numéros de test seulement » arrête les ajouts — ça ne retire personne de la liste.')
            : t('Seuls les numéros ci-dessous reçoivent une réponse automatique. Pour tous les autres, l’assistant prépare une réponse et attend un humain.')}
      </p>

      <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-4">
        {t('Numéros de test')}
      </h3>

      {/* ── Current list ── */}
      {numbers.length > 0 && (
        <div className="mt-3.5 space-y-2">
          {numbers.map((n) => {
            const c = canonicalPhone(n);
            return (
              <NumberRow
                key={n}
                display={c ? formatDisplay(c) : n}
                disabled={saveMut.isPending}
                onRemove={() => handleRemove(n)}
                onReset={() => handleReset(n)}
                resetting={resetOne.isPending && resetTarget === n}
              />
            );
          })}
        </div>
      )}

      {/* ── Add ── */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <label htmlFor="new-test-number" className="block text-[11px] font-semibold text-gray-500 mb-1">
          {t('Ajouter un numéro de test')}
        </label>
        <div className="flex gap-2 items-start">
          <input
            id="new-test-number" className={inputCls} value={raw}
            onChange={(e) => { setRaw(e.target.value); setFormError(null); }}
            placeholder="0623685162" inputMode="tel" autoComplete="tel"
            onKeyDown={(e) => { if (e.key === 'Enter' && trimmed) handleAdd(); }}
          />
          <Button size="sm" onClick={handleAdd} loading={saveMut.isPending} disabled={!trimmed}>
            <span className="inline-flex items-center gap-1.5"><PlusIcon className="w-4 h-4" />{t('Ajouter')}</span>
          </Button>
        </div>
        {/* Live normalisation preview — the owner sees it matched what they meant
            BEFORE saving, not after. */}
        {trimmed && valid && canon && (
          <p className="text-[11px] text-green-700 mt-1.5">
            {t('Sera enregistré comme : {n}', { n: formatDisplay(canon) })}
          </p>
        )}
        {trimmed && !valid && !formError && (
          <p className="text-[11px] text-gray-400 mt-1.5">
            {t('Formats acceptés : 0623685162, 212623685162, +212 623 68 51 62.')}
          </p>
        )}
        {formError && (
          <p className="text-[11.5px] text-red-600 mt-2 flex items-start gap-1.5" role="alert">
            <AlertIcon className="w-4 h-4 mt-px shrink-0" />
            <span>{formError}</span>
          </p>
        )}
      </div>
    </div>
  );
}
